import { useState, useEffect, useCallback, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { toast } from "../../components/AppToast";
import { getAllMenuList } from "../../services/admin/getAllMenu";
import {
  postModuleAccessRequest,
  getModuleAccessRequests,
  reviewModuleAccessRequest,
} from "../../services/global/global";

const normalizeModuleId = (m) => {
  if (m == null) return null;
  if (typeof m === "object" && m._id != null) return String(m._id);
  return String(m);
};

/** Build granted capability ids from API tenantModuleAccess and/or parent `modules` prop. */
function collectGrantedCapabilityIds(tenantModuleAccess, modulesProp) {
  const ids = new Set();
  (tenantModuleAccess || []).forEach((row) => {
    if (!row?.enabled) return;
    const id = row?.capability?._id;
    if (id != null) ids.add(String(id));
  });
  (modulesProp || []).forEach((item) => {
    if (!item?.enabled) return;
    const id = item?.capability?._id;
    if (id != null) ids.add(String(id));
  });
  return ids;
}

/**
 * Flatten `menus` tree: one option per node (parent and children).
 * Option value is menu `._id`. Rows tied to an already-granted capability are omitted.
 */
function menuRowsForRequest(menus, grantedCapabilityIds, extraRootMenuIds = new Set()) {
  const rows = [];
  const visit = (nodes, ancestorTitles, inheritedModuleId) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (node == null || node._id == null) continue;
      const moduleId = normalizeModuleId(node.module) || inheritedModuleId || null;
      const title = node.title || node.path || "(untitled)";
      const label = [...ancestorTitles, title].join(" › ");
      const nid = String(node._id);
      const blockedByCap = moduleId && grantedCapabilityIds.has(String(moduleId));
      const blockedByExtraRoot = extraRootMenuIds.has(nid);
      if (!blockedByCap && !blockedByExtraRoot) {
        rows.push({
          _id: nid,
          label,
          moduleId,
          menu: node,
        });
      }
      visit(node.children || [], [...ancestorTitles, title], moduleId);
    }
  };
  visit(menus || [], [], null);
  return rows;
}

function normalizePendingRequests(raw) {
  const data = raw?.data ?? raw;
  if (Array.isArray(data)) return data;
  if (Array.isArray(raw?.data?.data)) return raw.data.data;
  if (Array.isArray(data?.requests)) return data.requests;
  return [];
}

const ModuleAccessCard = ({
  modules,
  tenantExtraRootMenus = [],
  onSave,
  tenantId,
  userType,
  onRequestAccess,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [tempModules, setTempModules] = useState([...modules]);
  const isSuperAdmin = String(userType || "").toUpperCase() === "SUPER_ADMIN";

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [menuRequestOptions, setMenuRequestOptions] = useState([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState([]);
  const [requestMessage, setRequestMessage] = useState("");
  const [menuQuery, setMenuQuery] = useState("");
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewTargetReq, setReviewTargetReq] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    setTempModules([...modules]);
  }, [modules]);

  const extraRootMenuIds = useMemo(() => {
    const s = new Set();
    (tenantExtraRootMenus || []).forEach((m) => {
      if (m?._id != null) s.add(String(m._id));
    });
    return s;
  }, [tenantExtraRootMenus]);

  const loadPendingRequests = useCallback(async () => {
    if (!isSuperAdmin) return;
    setPendingLoading(true);
    try {
      const response = await getModuleAccessRequests("pending");
      setPendingRequests(normalizePendingRequests(response));
    } catch (err) {
      console.error(err);
      toast.error(
        typeof err === "string" ? err : "Failed to load pending module access requests."
      );
      setPendingRequests([]);
    } finally {
      setPendingLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadPendingRequests();
  }, [loadPendingRequests]);

  const loadMenuRequestOptions = useCallback(async () => {
    if (!tenantId) {
      toast.error("Tenant is not available; cannot load menus.");
      return;
    }
    setRequestLoading(true);
    try {
      const payload = await getAllMenuList({ tenantId });
      const granted = collectGrantedCapabilityIds(payload.tenantModuleAccess, modules);
      setMenuRequestOptions(
        menuRowsForRequest(payload.menus, granted, extraRootMenuIds)
      );
    } catch (err) {
      console.error(err);
      toast.error(typeof err === "string" ? err : "Failed to load menu list.");
      setMenuRequestOptions([]);
    } finally {
      setRequestLoading(false);
    }
  }, [tenantId, modules, extraRootMenuIds]);

  const openRequestModal = () => {
    setSelectedMenuIds([]);
    setRequestMessage("");
    setMenuQuery("");
    setRequestOpen(true);
    loadMenuRequestOptions();
  };

  const toggleMenuSelection = (id) => {
    const sid = String(id);
    setSelectedMenuIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const handleSubmitRequest = async () => {
    if (selectedMenuIds.length === 0) {
      toast.warn("Select at least one menu item to request.");
      return;
    }
    if (!tenantId) {
      toast.error("Tenant is not available.");
      return;
    }
    const idSet = new Set(selectedMenuIds.map(String));
    const selections = menuRequestOptions.filter((r) => idSet.has(r._id));
    const menuId = [...new Set(selections.map((s) => s._id))];
    const payload = {
      menuId,
      tenantId: String(tenantId),
    };
    const note = requestMessage.trim();
    if (note) payload.message = note;

    setSubmittingRequest(true);
    try {
      const response = await postModuleAccessRequest(payload);
      toast.success(
        typeof response?.message === "string" ? response.message : "Module access request submitted."
      );
      onRequestAccess?.({
        menuIds: menuId,
        selections: selections.map((row) => ({
          menuId: row._id,
          menu: row.menu,
          moduleId: row.moduleId,
          label: row.label,
        })),
        response,
      });
      setRequestOpen(false);
      setRequestMessage("");
    } catch (err) {
      console.error(err);
      toast.error(typeof err === "string" ? err : "Failed to submit module access request.");
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleChange = (index, checked) => {
    const updated = [...tempModules];
    updated[index].enabled = checked;
    setTempModules(updated);
  };

  const getTenantName = (req) =>
    req?.tenant?.tenant_name ||
    req?.tenantId?.tenant_name ||
    req?.tenantName ||
    req?.tenantId?.tenant_name ||
    req?.tenant_id?.tenant_name ||
    "Unknown tenant";

  const getRequestId = (req) => String(req?._id || req?.id || "");

  const getMenuIds = (req) => {
    const raw = req?.menuId ?? req?.menuIds ?? req?.menu;
    if (Array.isArray(raw)) {
      return raw.map((item) => {
        if (item && typeof item === "object") {
          const title = item.title || item.path || item._id || "-";
          return `${title} (${item._id || "-"})`;
        }
        return String(item);
      });
    }
    if (raw && typeof raw === "object") {
      const title = raw.title || raw.path || raw._id || "-";
      return [`${title} (${raw._id || "-"})`];
    }
    if (raw == null) return [];
    return [String(raw)];
  };

  const getRequestedByText = (req) => {
    const user = req?.requestedBy;
    if (!user) return "-";
    const name = (user.firstName || "").trim() || user.username || user.email || "Unknown";
    const meta = [user.userType, user.username || user.email].filter(Boolean).join(" • ");
    return meta ? `${name} (${meta})` : name;
  };

  const openReviewModal = (req, action) => {
    const requestId = getRequestId(req);
    if (!requestId) {
      toast.error("Request id is missing.");
      return;
    }
    setReviewTargetReq(req);
    setReviewAction(action);
    setReviewNote(
      action === "approve"
        ? "Approved by super admin"
        : "Rejected by super admin"
    );
    setReviewModalOpen(true);
  };

  const closeReviewModal = () => {
    if (reviewSubmitting) return;
    setReviewModalOpen(false);
    setReviewTargetReq(null);
    setReviewAction(null);
    setReviewNote("");
  };

  const confirmReview = async () => {
    const req = reviewTargetReq;
    const action = reviewAction;
    if (!req || !action) return;
    const requestId = getRequestId(req);
    if (!requestId) {
      toast.error("Request id is missing.");
      return;
    }
    setReviewSubmitting(true);
    setActionLoadingId(requestId);
    try {
      await reviewModuleAccessRequest(requestId, action, reviewNote.trim());
      setPendingRequests((prev) =>
        prev.filter((item) => getRequestId(item) !== requestId)
      );
      toast.success(
        action === "approve"
          ? "Request approved successfully."
          : "Request rejected successfully."
      );
      setReviewModalOpen(false);
      setReviewTargetReq(null);
      setReviewAction(null);
      setReviewNote("");
    } catch (err) {
      console.error(err);
      toast.error(
        typeof err === "string"
          ? err
          : `Failed to ${action === "approve" ? "approve" : "reject"} request.`
      );
    } finally {
      setReviewSubmitting(false);
      setActionLoadingId("");
    }
  };

  const isApproveReview = reviewAction === "approve";

  const closeEditor = () => {
    setTempModules([...modules]);
    setEditMode(false);
  };

  return (
    <>
    <div className="admin-block">
      <div className="admin-toolbar">
        <p>
          {editMode
            ? "Turn modules on or off, then save. Disabled modules stay hidden from this tenant."
            : "Enabled modules are available in the tenant menu."}
        </p>
        <div className="admin-toolbar__actions">
          {!isSuperAdmin ? (
            <button
              type="button"
              className="settings-btn settings-btn--ghost"
              onClick={openRequestModal}
              disabled={!tenantId}
            >
              <i className="bi bi-plus-circle" aria-hidden="true" />
              Ask for menus
            </button>
          ) : (
            <button
              type="button"
              className="settings-btn settings-btn--ghost"
              onClick={loadPendingRequests}
              disabled={pendingLoading}
            >
              {pendingLoading ? "Refreshing" : "Refresh requests"}
            </button>
          )}
          {editMode ? (
            <button type="button" className="settings-btn settings-btn--ghost" onClick={closeEditor}>
              Cancel
            </button>
          ) : (
            <button type="button" className="settings-btn settings-btn--primary" onClick={() => setEditMode(true)}>
              Edit modules
            </button>
          )}
        </div>
      </div>
        {isSuperAdmin && (
          <section className="admin-requests">
            <div className="admin-toolbar">
              <h3>Pending requests</h3>
            </div>
            {pendingLoading ? (
              <p className="settings-footnote">Loading pending requests.</p>
            ) : pendingRequests.length === 0 ? (
              <p className="settings-footnote">No pending requests.</p>
            ) : (
              <div className="admin-request-list">
                {pendingRequests.map((req) => {
                  const reqId = getRequestId(req);
                  const menuIds = getMenuIds(req);
                  const actionBusy = actionLoadingId === reqId;
                  return (
                    <article key={reqId} className="admin-request">
                      <header>
                        <strong>{getTenantName(req)}</strong>
                        <span>{req?.createdAt ? new Date(req.createdAt).toLocaleString() : "Date unavailable"}</span>
                      </header>
                      <p>{menuIds.length > 0 ? menuIds.join(", ") : "No menus listed"}</p>
                      <p>Requested by {getRequestedByText(req)}</p>
                      {req?.message ? <p>{req.message}</p> : null}
                      <div className="admin-request__actions">
                        <button
                          type="button"
                          className="settings-btn settings-btn--primary"
                          onClick={() => openReviewModal(req, "approve")}
                          disabled={actionBusy}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="settings-btn settings-btn--ghost"
                          onClick={() => openReviewModal(req, "reject")}
                          disabled={actionBusy}
                        >
                          Reject
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {Array.isArray(tenantExtraRootMenus) && tenantExtraRootMenus.length > 0 && (
          <section className="admin-extra">
            <div className="admin-toolbar">
              <div>
                <h3>Extra menus</h3>
                <p>Granted from approved requests. These stay on and are separate from the toggles below.</p>
              </div>
              <span className="admin-status is-on">Always on</span>
            </div>
            <ul>
              {tenantExtraRootMenus.map((m) => (
                <li key={String(m._id)}>
                  <strong>{m.title || m.path || "Menu"}</strong>
                  {m.path ? <code>{m.path}</code> : null}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="settings-toggle-list">
          {tempModules?.map((item, index) => {
            const title = item?.capability?.value || "Module";
            const body = (
              <>
                <span className="settings-toggle__icon" aria-hidden="true">
                  <i className="bi bi-grid" />
                </span>
                <span className="settings-toggle__copy">
                  <strong>{title}</strong>
                  <small>{item.enabled ? "Available in the tenant menu" : "Hidden from this tenant"}</small>
                </span>
                {editMode ? (
                  <span className="settings-switch" aria-hidden="true"><span /></span>
                ) : (
                  <span className={`admin-status${item.enabled ? " is-on" : ""}`}>
                    {item.enabled ? "Enabled" : "Off"}
                  </span>
                )}
              </>
            );

            if (!editMode) {
              return (
                <article className={`settings-toggle admin-module${item.enabled ? " is-on" : ""}`} key={item?.capability?._id}>
                  {body}
                </article>
              );
            }

            return (
              <button
                type="button"
                className={`settings-toggle${item.enabled ? " is-on" : ""}`}
                key={item?.capability?._id}
                onClick={() => handleChange(index, !item.enabled)}
                aria-pressed={Boolean(item.enabled)}
              >
                {body}
              </button>
            );
          })}
        </div>

        {editMode && (
          <div className="settings-actions">
            <button
              type="button"
              className="settings-btn settings-btn--primary"
              onClick={() => {
                onSave(tempModules);
                setEditMode(false);
              }}
            >
              Save modules
            </button>
          </div>
        )}
    </div>

      {isSuperAdmin && (
        <Modal
          show={reviewModalOpen}
          onHide={closeReviewModal}
          centered
          backdrop={reviewSubmitting ? "static" : true}
          keyboard={!reviewSubmitting}
          contentClassName="border-0 shadow-lg rounded-4 overflow-hidden"
        >
          <div
            className="w-100"
            style={{
              height: 5,
              background: isApproveReview
                ? "linear-gradient(90deg, #198754 0%, #20c997 50%, #0d6efd 100%)"
                : "linear-gradient(90deg, #dc3545 0%, #fd7e14 50%, #6f42c1 100%)",
            }}
            aria-hidden
          />
          <Modal.Header className="border-0 pb-0 pt-4 px-4">
            <div className="d-flex align-items-start gap-3 w-100">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 48,
                  height: 48,
                  background: isApproveReview
                    ? "rgba(25, 135, 84, 0.12)"
                    : "rgba(220, 53, 69, 0.12)",
                  color: isApproveReview ? "#198754" : "#dc3545",
                }}
              >
                <i
                  className={`bi ${isApproveReview ? "bi-check2-circle" : "bi-slash-circle"} fs-4`}
                  aria-hidden
                />
              </div>
              <div className="min-w-0 flex-grow-1">
                <Modal.Title className="fs-5 fw-semibold mb-1">
                  {isApproveReview ? "Approve access request" : "Reject access request"}
                </Modal.Title>
                <p className="text-muted small mb-0">
                  {isApproveReview
                    ? "Confirm approval and add an optional note for the tenant."
                    : "Reject this request with a clear reason (recommended)."}
                </p>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="px-4 pt-3 pb-2">
            {reviewTargetReq ? (
              <div
                className="rounded-3 p-3 mb-3 small"
                style={{
                  background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                }}
              >
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <span className="badge rounded-pill bg-white text-dark border">
                    <i className="bi bi-building me-1" aria-hidden />
                    {getTenantName(reviewTargetReq)}
                  </span>
                  <span
                    className={`badge rounded-pill ${
                      isApproveReview ? "text-bg-success" : "text-bg-danger"
                    }`}
                  >
                    {isApproveReview ? "Approve" : "Reject"}
                  </span>
                </div>
                <p className="mb-1 text-break">
                  <span className="text-secondary">Menu · </span>
                  <span className="fw-medium">
                    {getMenuIds(reviewTargetReq).join(" · ") || "—"}
                  </span>
                </p>
                <p className="mb-0 text-muted">
                  <span className="text-secondary">Requested by · </span>
                  {getRequestedByText(reviewTargetReq)}
                </p>
              </div>
            ) : null}
            <Form.Group className="mb-0">
              <Form.Label className="fw-medium small">
                Message for the tenant{" "}
                <span className="text-muted fw-normal">(optional)</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder={
                  isApproveReview
                    ? "e.g. Approved — menu will appear after next login."
                    : "e.g. Not allowed for this tenant at this time."
                }
                disabled={reviewSubmitting}
                className="shadow-sm"
                style={{ resize: "vertical", minHeight: 88 }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 px-4 pb-4 pt-0 gap-2">
            <Button
              variant="light"
              className="border"
              onClick={closeReviewModal}
              disabled={reviewSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant={isApproveReview ? "success" : "danger"}
              onClick={confirmReview}
              disabled={reviewSubmitting}
              className="px-4"
            >
              {reviewSubmitting
                ? "Submitting…"
                : isApproveReview
                  ? "Confirm approve"
                  : "Confirm reject"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {!isSuperAdmin && (
        <Modal
          show={requestOpen}
          onHide={() => {
            if (!submittingRequest) setRequestOpen(false);
          }}
          centered
          backdrop={submittingRequest ? "static" : true}
          keyboard={!submittingRequest}
          dialogClassName="admin-request-dialog"
          contentClassName="admin-request-modal"
          aria-labelledby="request-module-title"
        >
          <div className="admin-request-modal__head">
            <div>
              <p className="settings-kicker">Access</p>
              <h2 id="request-module-title">Ask for menus</h2>
              <p>Choose the menus this tenant still needs. Menus you already have stay hidden.</p>
            </div>
            <button
              type="button"
              className="admin-request-modal__close"
              aria-label="Close"
              onClick={() => setRequestOpen(false)}
              disabled={submittingRequest}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {requestLoading ? (
            <p className="admin-request-modal__status">Loading menus</p>
          ) : menuRequestOptions.length === 0 ? (
            <p className="admin-request-modal__status">No additional menus are available to request.</p>
          ) : (
            <>
              <label className="admin-request-search">
                <i className="bi bi-search" aria-hidden="true" />
                <input
                  type="search"
                  value={menuQuery}
                  onChange={(event) => setMenuQuery(event.target.value)}
                  placeholder="Search menus"
                  disabled={submittingRequest}
                />
              </label>

              <div className="admin-request-modal__meta">
                <span>{selectedMenuIds.length} selected</span>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      const query = menuQuery.trim().toLowerCase();
                      const visible = menuRequestOptions.filter((row) =>
                        row.label.toLowerCase().includes(query)
                      );
                      setSelectedMenuIds((current) => [
                        ...new Set([...current, ...visible.map((row) => row._id)]),
                      ]);
                    }}
                  >
                    Select all
                  </button>
                  <button type="button" onClick={() => setSelectedMenuIds([])}>
                    Clear
                  </button>
                </div>
              </div>

              <div className="admin-menu-list" role="group" aria-label="Menus to request">
                {menuRequestOptions
                  .filter((row) => row.label.toLowerCase().includes(menuQuery.trim().toLowerCase()))
                  .map((row) => {
                    const selected = selectedMenuIds.includes(row._id);
                    return (
                      <label
                        key={row._id}
                        className={`admin-menu-option${selected ? " is-on" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleMenuSelection(row._id)}
                          disabled={submittingRequest}
                        />
                        <span className="admin-menu-option__mark" aria-hidden="true">
                          <i className="bi bi-check-lg" />
                        </span>
                        <span>{row.label}</span>
                      </label>
                    );
                  })}
                {menuRequestOptions.every(
                  (row) => !row.label.toLowerCase().includes(menuQuery.trim().toLowerCase())
                ) ? (
                  <p className="admin-request-modal__status">No menus match that search.</p>
                ) : null}
              </div>

              <label className="settings-field admin-request-note" htmlFor="module-request-note">
                <span className="settings-field__label">
                  Note
                  <em>Optional</em>
                </span>
                <textarea
                  id="module-request-note"
                  className="settings-input"
                  rows={3}
                  value={requestMessage}
                  onChange={(event) => setRequestMessage(event.target.value)}
                  placeholder="Tell the admin why you need these menus"
                  disabled={submittingRequest}
                />
              </label>
            </>
          )}

          <div className="admin-request-modal__actions">
            <button
              type="button"
              className="settings-btn settings-btn--ghost"
              onClick={() => setRequestOpen(false)}
              disabled={submittingRequest}
            >
              Cancel
            </button>
            <button
              type="button"
              className="settings-btn settings-btn--primary"
              onClick={handleSubmitRequest}
              disabled={requestLoading || submittingRequest || selectedMenuIds.length === 0}
            >
              <i className="bi bi-send" aria-hidden="true" />
              {submittingRequest ? "Sending" : "Send request"}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ModuleAccessCard;
